import React, { useEffect, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { FaHandHoldingUsd } from 'react-icons/fa';
import { toast } from '../../toasts';
import commissionService from '../../services/commission.service';
import UserService from '../../services/user.service';
import CustomTypeahead from '../../components/CustomTypeahead';
import { getError } from '../../utils';
import { Button, Checkbox, FormField, Select, controlClass, textareaClass } from '../../components/EntityList';

const CURRENCIES = ['USD', 'UYU'];
const CALCULATION_BASES = [
	{ value: 'standalone', label: 'Sobre precio base' },
	{ value: 'compound', label: 'Sobre base + IRPF' },
];
const REQUIRED_FIELD_MESSAGE = 'Este campo es obligatorio.';
const FIELD_LABELS = {
	externalSellerName: 'Nombre del vendedor externo',
	amount: 'Monto',
	currency: 'Moneda',
};

const FieldError = ({ message }) =>
	message ? <p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-danger'>{message}</p> : null;

/**
 * Modal form for creating/editing a commission.
 * Props:
 *   show, onHide, onSaved
 *   initialData: commission object (for editing) or null
 *   contractId / assetSaleId: pre-linked entity
 *   parentAmount: number | null — used to auto-calc commission from percentage
 *   parentCurrency: string | null
 */
const CommissionEditor = ({ show, onHide, onSaved, initialData, contractId, assetSaleId, parentAmount, parentCurrency }) => {
	const isEditing = !!initialData?._id;

	const [seller, setSeller] = useState(null);
	const [isExternalSeller, setIsExternalSeller] = useState(false);
	const [externalSellerName, setExternalSellerName] = useState('');
	const [amount, setAmount] = useState('');
	const [currency, setCurrency] = useState(parentCurrency || 'USD');
	const [percentage, setPercentage] = useState('');
	const [observations, setObservations] = useState('');
	const [calculationBase, setCalculationBase] = useState('standalone');
	const [users, setUsers] = useState([]);
	const [saving, setSaving] = useState(false);
	const [fieldErrors, setFieldErrors] = useState({});
	const [formError, setFormError] = useState('');

	useEffect(() => {
		UserService.list().then((res) => setUsers(res.data || [])).catch(() => {});
	}, []);

	useEffect(() => {
		if (initialData) {
			setSeller(initialData.seller || null);
			setIsExternalSeller(initialData.isExternalSeller || false);
			setExternalSellerName(initialData.externalSellerName || '');
			setAmount(initialData.amount || '');
			setCurrency(initialData.currency || parentCurrency || 'USD');
			setPercentage(initialData.percentage != null ? String(initialData.percentage) : '');
			setObservations(initialData.observations || '');
			setCalculationBase(initialData.calculationBase || 'standalone');
		} else {
			setSeller(null);
			setIsExternalSeller(false);
			setExternalSellerName('');
			setAmount('');
			setCurrency(parentCurrency || 'USD');
			setPercentage('');
			setObservations('');
			setCalculationBase('standalone');
		}
		// Never carry a stale validation state into a reopened modal.
		setFieldErrors({});
		setFormError('');
	}, [initialData, parentCurrency]);

	const clearFieldError = (field) => setFieldErrors((prev) => {
		if (!prev[field]) return prev;
		const next = { ...prev };
		delete next[field];
		return next;
	});

	const getFieldErrors = () => {
		const errors = {};
		if (isExternalSeller && !externalSellerName.trim()) errors.externalSellerName = REQUIRED_FIELD_MESSAGE;
		if (!String(amount).trim()) errors.amount = REQUIRED_FIELD_MESSAGE;
		if (!currency) errors.currency = REQUIRED_FIELD_MESSAGE;
		return errors;
	};

	// Auto-calculate amount when percentage changes
	const handlePercentageChange = (val) => {
		setPercentage(val);
		if (parentAmount && val) {
			const calc = (parseFloat(parentAmount) * parseFloat(val)) / 100;
			if (!isNaN(calc)) {
				setAmount(calc.toFixed(2));
				clearFieldError('amount');
			}
		}
	};

	const handleExternalToggle = (checked) => {
		setIsExternalSeller(checked);
		if (checked) {
			setSeller(null);
		} else {
			setExternalSellerName('');
		}
		setFieldErrors({});
	};

	const handleSave = async () => {
		const errors = getFieldErrors();
		const missingFields = Object.keys(errors);
		if (missingFields.length > 0) {
			setFieldErrors(errors);
			const message = `Complete el/los siguiente/s campo/s requerido/s: ${missingFields
				.map((field) => FIELD_LABELS[field] || field)
				.join(', ')}.`;
			setFormError(message);
			toast.error(message);
			return;
		}
		setFieldErrors({});
		setFormError('');

		try {
			setSaving(true);
			const payload = {
				seller: isExternalSeller ? undefined : (seller?._id || seller || undefined),
				isExternalSeller,
				externalSellerName: isExternalSeller ? externalSellerName : undefined,
				amount: Number(amount),
				currency,
				percentage: percentage ? Number(percentage) : undefined,
				calculationBase,
				observations,
				...(contractId ? { contract: contractId } : {}),
				...(assetSaleId ? { assetSale: assetSaleId } : {}),
			};

			if (isEditing) {
				await commissionService.edit(initialData._id, payload);
				toast.success('Comisión actualizada');
			} else {
				await commissionService.create(payload);
				toast.success('Comisión registrada');
			}
			onSaved();
		} catch (err) {
			setFormError(getError(err));
			toast.error(getError(err));
		} finally {
			setSaving(false);
		}
	};

	const subtitle = parentAmount
		? `Se calcula sobre ${parentCurrency ? `${parentCurrency} ` : ''}${parentAmount}`
		: isEditing
			? 'Actualizá los datos de la comisión.'
			: 'Completá los datos para registrar la comisión.';

	return (
		<Modal
			show={show}
			onHide={onHide}
			centered
			backdrop={saving ? 'static' : true}
			keyboard={!saving}
			dialogClassName='tw:max-w-[720px]'
			contentClassName='entity-list-page el-modal tw:rounded-[14px] tw:border-0 tw:overflow-hidden tw:shadow-overlay tw:font-sans tw:text-[14px]/[1.45] tw:text-ink-0'
		>
			<Modal.Header
				closeButton={!saving}
				className='tw:items-start tw:border-b tw:border-border tw:px-[22px] tw:pt-[20px] tw:pb-[14px]'
			>
				<div className='tw:flex tw:items-start tw:gap-3'>
					<span className='tw:mt-0.5 tw:flex tw:h-[38px] tw:w-[38px] tw:shrink-0 tw:items-center tw:justify-center tw:rounded-[9px] tw:bg-accent-soft tw:text-accent'>
						<FaHandHoldingUsd />
					</span>
					<div className='tw:min-w-0'>
						<Modal.Title className='tw:text-[17px]/[1.3] tw:font-bold tw:tracking-[-0.01em] tw:text-ink-0'>
							{isEditing ? 'Editar comisión' : 'Nueva comisión'}
						</Modal.Title>
						<p className='tw:mb-0 tw:mt-1 tw:text-[12.5px] tw:text-ink-2'>{subtitle}</p>
					</div>
				</div>
			</Modal.Header>

			<Modal.Body className='tw:flex tw:flex-col tw:gap-3.5 tw:px-[22px] tw:py-[18px]'>
				{formError && (
					<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-4 tw:py-3 tw:text-[13px] tw:text-danger'>
						{formError}
					</div>
				)}
				<Form
					onSubmit={(e) => { e.preventDefault(); handleSave(); }}
					className='tw:flex tw:flex-col tw:gap-3.5'
				>
					<Checkbox
						id='commission-external-seller'
						label='Vendedor externo'
						checked={isExternalSeller}
						onChange={(e) => handleExternalToggle(e.target.checked)}
						disabled={saving}
					/>

					{isExternalSeller ? (
						<FormField id='commission-external-seller-name' label='Nombre del vendedor externo' required>
							<Form.Control
								id='commission-external-seller-name'
								type='text'
								className={controlClass}
								value={externalSellerName}
								onChange={(e) => { setExternalSellerName(e.target.value); clearFieldError('externalSellerName'); }}
								placeholder='Nombre completo del vendedor'
								disabled={saving}
							/>
							<FieldError message={fieldErrors.externalSellerName} />
						</FormField>
					) : (
						<FormField id='commission-seller' label='Vendedor / Intermediario'>
							<CustomTypeahead
								id='commission-seller'
								variant='entity'
								labelKey={(opt) => opt.name || ''}
								options={users}
								selected={seller ? [seller] : []}
								onChange={(sel) => setSeller(sel[0] || null)}
								placeholder='Buscar usuario...'
								disabled={saving}
							/>
						</FormField>
					)}

					<div className='tw:grid tw:grid-cols-1 tw:gap-4 tw:sm:grid-cols-3'>
						<FormField id='commission-percentage' label='Porcentaje (%)'>
							<Form.Control
								id='commission-percentage'
								type='number'
								min='0'
								max='100'
								step='0.01'
								className={controlClass}
								value={percentage}
								onChange={(e) => handlePercentageChange(e.target.value)}
								placeholder='0.00'
								disabled={saving}
							/>
						</FormField>
						<FormField id='commission-amount' label='Monto' required>
							<Form.Control
								id='commission-amount'
								type='number'
								min='0'
								step='0.01'
								className={controlClass}
								value={amount}
								onChange={(e) => { setAmount(e.target.value); clearFieldError('amount'); }}
								placeholder='0.00'
								disabled={saving}
							/>
							<FieldError message={fieldErrors.amount} />
						</FormField>
						<FormField id='commission-currency' label='Moneda' required>
							<Select
								id='commission-currency'
								options={CURRENCIES}
								value={currency}
								onChange={(e) => { setCurrency(e.target.value); clearFieldError('currency'); }}
								disabled={saving}
							/>
							<FieldError message={fieldErrors.currency} />
						</FormField>
					</div>

					<div className='tw:grid tw:grid-cols-1 tw:gap-4 tw:sm:grid-cols-2'>
						<FormField id='commission-calculation-base' label='Base de cálculo'>
							<Select
								id='commission-calculation-base'
								options={CALCULATION_BASES}
								value={calculationBase}
								onChange={(e) => setCalculationBase(e.target.value)}
								disabled={saving}
							/>
						</FormField>
					</div>

					<FormField id='commission-observations' label='Observaciones'>
						<Form.Control
							id='commission-observations'
							as='textarea'
							rows={3}
							className={textareaClass}
							value={observations}
							onChange={(e) => setObservations(e.target.value)}
							disabled={saving}
						/>
					</FormField>
				</Form>
			</Modal.Body>

			<Modal.Footer className='tw:border-t tw:border-border tw:bg-surface-2 tw:px-[22px] tw:py-[14px] tw:gap-2'>
				<Button onClick={onHide} disabled={saving}>Cancelar</Button>
				<Button variant='primary' onClick={handleSave} disabled={saving}>
					{saving ? 'Guardando...' : 'Guardar'}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default CommissionEditor;
